import React, { useState } from 'react';
import { Trash2, RefreshCw, Clock, Search, ArrowLeft, ChevronLeft, ChevronRight, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
} from '@license-management/ui';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/Toast';
import { useDeletedApiKeys, type DeletedApiKey } from '../api/hooks/apikeys/useApiKeyQuery';
import { useRestoreApiKey, usePermanentlyDeleteApiKey } from '../api/hooks/apikeys/useApiKeyMutation';

export function ApiKeyTrash() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKey, setSelectedKey] = useState<DeletedApiKey | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] = useState(false);

  // Fetch deleted API keys
  const { data, isLoading, error } = useDeletedApiKeys({
    page,
    pageSize: 20,
    search: searchTerm,
  });

  // Restore mutation
  const restoreMutation = useRestoreApiKey();

  // Permanent delete mutation
  const permanentDeleteMutation = usePermanentlyDeleteApiKey();

  // Handle restore success/error
  React.useEffect(() => {
    if (restoreMutation.isSuccess) {
      setShowRestoreDialog(false);
      setSelectedKey(null);
      showToast('API key restored successfully. Note: The key remains revoked.', 'success');
    }
    if (restoreMutation.isError) {
      showToast(restoreMutation.error?.message || 'Failed to restore API key', 'error');
    }
  }, [restoreMutation.isSuccess, restoreMutation.isError]);

  // Handle permanent delete success/error
  React.useEffect(() => {
    if (permanentDeleteMutation.isSuccess) {
      setShowPermanentDeleteDialog(false);
      setSelectedKey(null);
      showToast('API key permanently deleted', 'success');
    }
    if (permanentDeleteMutation.isError) {
      showToast(permanentDeleteMutation.error?.message || 'Failed to permanently delete API key', 'error');
    }
  }, [permanentDeleteMutation.isSuccess, permanentDeleteMutation.isError]);

  const getDaysUntilDeletionVariant = (days: number): 'error' | 'warning' | 'default' => {
    if (days <= 7) return 'error';
    if (days <= 14) return 'warning';
    return 'default';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message="Failed to load deleted API keys" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="API Key Recycle Bin"
        description="Deleted API keys are kept for 30 days before permanent deletion"
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/api-keys')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to API Keys
          </Button>
        }
      />

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            leftElement={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* API Keys table */}
      {data?.keys.length === 0 ? (
        <Card className="text-center py-12">
          <Key className="mx-auto h-12 w-12 text-zinc-400 mb-4" />
          <h3 className="text-lg font-medium text-zinc-900 mb-2">No deleted API keys</h3>
          <p className="text-zinc-500">The recycle bin is empty.</p>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-200">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      API Key
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Deleted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Time Until Deletion
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-zinc-200">
                  {data?.keys.map((apiKey) => (
                    <tr key={apiKey.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Key className="w-5 h-5 text-zinc-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-zinc-900">
                              {apiKey.name}
                            </div>
                            <code className="text-xs text-zinc-500">
                              {apiKey.keyPrefix}...
                            </code>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-zinc-900">
                          {apiKey.userEmail}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-zinc-900">
                            {new Date(apiKey.deletedAt).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-zinc-500">
                            by {apiKey.deletedByEmail || 'Unknown'}
                          </div>
                          {apiKey.deletionReason && (
                            <div className="text-sm text-zinc-400 italic">
                              "{apiKey.deletionReason}"
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getDaysUntilDeletionVariant(apiKey.daysUntilPermanentDeletion)}>
                          <Clock className="h-3 w-3 mr-1" />
                          {apiKey.daysUntilPermanentDeletion} days
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedKey(apiKey);
                              setShowRestoreDialog(true);
                            }}
                            leftIcon={<RefreshCw className="h-4 w-4" />}
                          >
                            Restore
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setSelectedKey(apiKey);
                              setShowPermanentDeleteDialog(true);
                            }}
                            leftIcon={<Trash2 className="h-4 w-4" />}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-700">
                Showing {(data.page - 1) * data.pageSize + 1} to{' '}
                {Math.min(data.page * data.pageSize, data.totalCount)} of{' '}
                {data.totalCount} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  leftIcon={<ChevronLeft className="h-4 w-4" />}
                >
                  Previous
                </Button>
                <span className="text-sm text-zinc-700">
                  Page {data.page} of {data.totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === data.totalPages}
                  rightIcon={<ChevronRight className="h-4 w-4" />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Restore Dialog */}
      <ConfirmDialog
        isOpen={showRestoreDialog}
        onCancel={() => {
          setShowRestoreDialog(false);
          setSelectedKey(null);
        }}
        onConfirm={() => {
          if (selectedKey) {
            restoreMutation.mutate(selectedKey.id);
          }
        }}
        title="Restore API Key"
        message={`Are you sure you want to restore the API key "${selectedKey?.name}"? Note: The key will remain revoked after restoration. You can reactivate it from the main page.`}
        confirmLabel="Restore"
      />

      {/* Permanent Delete Dialog */}
      <ConfirmDialog
        isOpen={showPermanentDeleteDialog}
        onCancel={() => {
          setShowPermanentDeleteDialog(false);
          setSelectedKey(null);
        }}
        onConfirm={() => {
          if (selectedKey) {
            permanentDeleteMutation.mutate({ keyId: selectedKey.id });
          }
        }}
        title="Permanently Delete API Key"
        message={`This action cannot be undone! Are you sure you want to permanently delete the API key "${selectedKey?.name}"? This will remove all associated usage data and history.`}
        confirmLabel="Permanently Delete"
        severity="high"
      />
    </div>
  );
}
